import { Navigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthProvider';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { supabase } from '@/integrations/supabase/client';
import AdminLockScreen from '@/components/admin/AdminLockScreen';

export default function AdminProtected({ children }: { children: JSX.Element }) {
  const { loading, user } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const [lockChecked, setLockChecked] = useState(false);
  const [locked, setLocked] = useState(false);

  const checkLock = useCallback(async () => {
    try {
      const { data } = await supabase.rpc('get_admin_lock_state');
      setLocked(Boolean(data));
    } catch {
      setLocked(false);
    } finally {
      setLockChecked(true);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setLockChecked(true);
      return;
    }
    // Initial check
    checkLock();
    // Poll every 5s so a Telegram lock kicks all admins out within 5 seconds
    const intervalId = window.setInterval(checkLock, 5000);
    // Re-check immediately when tab becomes visible / window regains focus
    const onVisible = () => { if (document.visibilityState === 'visible') checkLock(); };
    const onFocus = () => checkLock();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAdmin, checkLock]);

  if (loading || adminLoading || (isAdmin && !lockChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-app))' }}>
        <div className="text-ivory/60">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (locked) {
    return <AdminLockScreen onUnlocked={() => setLocked(false)} />;
  }

  return children;
}
